import axios from 'axios';
import { githubOrgRead, GithubReadError } from '@/apis/githubRead';
import { resolveGitlabBaseUrl } from '@/helpers/resolveGitlabBaseUrl';

interface GithubTeam {
  name: string;
  id: number;
  slug: string;
}

interface Team {
  TeamName: string;
  GitTeamID: number;
  /** GitHub only. Team membership is read by org plus slug, because GitHub's
   * team-by-id route is closing down and carries no org to authorize against.
   * GitLab keeps using the numeric id its own API expects. */
  GitTeamSlug?: string;
}

/**
 * Maps a failed backend read onto what this module already surfaces: callers
 * branch on an error that stringifies to 401/403 to show the access-denied
 * path, so those statuses stay as numbers and everything else becomes copy that
 * is safe to show a user.
 *
 * `integration_missing` (the PR bot is not installed for this org) is reported
 * as 403 because the caller's dead end is the same: Entelligence is not allowed
 * to read the org and an admin has to grant that.
 */
function describeOrgReadFailure(error: unknown): number | string {
  if (error instanceof GithubReadError) {
    if (error.status === 401) return 401;
    if (error.status === 403 || error.code === 'integration_missing')
      return 403;
  }
  return 'We could not load team information from GitHub. Please try again in a moment.';
}

/**
 * Teams in the GitHub org, read through the backend.
 *
 * @param authToken - Unused. The backend resolves the GitHub credential, so the
 * browser holds none; kept so existing call sites stay unchanged.
 */
export async function importTeamsFromGh(
  orgName: string | undefined,
  authToken: string
): Promise<{ teams: Team[]; error: any }> {
  if (!orgName) {
    return { teams: [], error: null };
  }

  try {
    const teams: Team[] = [];
    let page = 1;
    let hasMorePages = true;

    while (hasMorePages) {
      const { data, has_more } = await githubOrgRead<GithubTeam[]>({
        op: 'org_teams',
        org: orgName,
        page,
        perPage: 100,
      });

      const githubTeams = Array.isArray(data) ? data : [];
      // The backend derives `has_more` from GitHub's Link header, so trust it
      // instead of inferring the end of the list from a short page.
      hasMorePages = has_more;
      page += 1;

      teams.push(
        ...githubTeams.map((team) => ({
          TeamName: team.name,
          GitTeamID: team.id,
          GitTeamSlug: team.slug,
        }))
      );
    }

    return { teams, error: null };
  } catch (error) {
    console.error('Error importing teams from GitHub:', error);
    return { teams: [], error: describeOrgReadFailure(error) };
  }
}

/**
 * Members of one GitHub team, read through the backend.
 *
 * Identified by org plus team slug: GitHub's `/teams/{id}/members` route is
 * closing down, and a bare numeric id carries no organization, so the backend
 * had no way to check the team belonged to the caller's workspace.
 *
 * @param orgName - GitHub org login that owns the team.
 * @param teamSlug - The team's slug, from `importTeamsFromGh`.
 */
export async function getTeamMembersFromGh(
  orgName: string | undefined,
  teamSlug: string | undefined
): Promise<{ members: GithubMember[]; error: any }> {
  if (!orgName || !teamSlug) {
    return { members: [], error: null };
  }

  try {
    const { data } = await githubOrgRead<GithubMember[]>({
      op: 'team_members',
      org: orgName,
      teamSlug,
      perPage: 100,
    });

    const members = Array.isArray(data) ? data : [];

    // The login stays in `login`. It is deliberately not copied into `name`:
    // GitHub's member listing has no display name, and a login stored as
    // someone's name is indistinguishable from a real one afterwards. Callers
    // fall back to `login` when they render an unnamed member.
    const teamMembers = members.map((member) => ({
      login: member.login,
      avatar_url: member.avatar_url,
      name: member.name ?? '',
    }));

    return { members: teamMembers, error: null };
  } catch (error) {
    console.error('Error fetching team members:', error);
    return { members: [], error: describeOrgReadFailure(error) };
  }
}

export async function importTeamsFromGl(
  groupPath: string | undefined,
  authToken: string,
  authUrl?: string
): Promise<{ teams: Team[]; error: any }> {
  if (!groupPath) {
    return { teams: [], error: null };
  }

  try {
    const base = resolveGitlabBaseUrl(authUrl);
    const teams: Team[] = [];
    let page = 1;
    let hasMorePages = true;

    while (hasMorePages) {
      const responseForSubGroups = await axios.get(
        `${base}/api/v4/groups/${encodeURIComponent(groupPath)}/subgroups?per_page=100&page=${page}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const responseForGroups = await axios.get(
        `${base}/api/v4/groups?per_page=100&page=${page}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (
        responseForSubGroups.status === 401 ||
        responseForSubGroups.status === 403
      ) {
        return { teams: [], error: responseForSubGroups.status };
      }

      if (
        responseForGroups.status === 401 ||
        responseForGroups.status === 403
      ) {
        return { teams: [], error: responseForGroups.status };
      }

      const gitlabGroups: GithubTeam = await responseForGroups.data;
      const gitlabSubGroups: GithubTeam[] = await responseForSubGroups.data;

      if (gitlabSubGroups.length < 100) {
        hasMorePages = false;
      } else {
        page++;
      }

      teams.push({
        TeamName: gitlabGroups.name,
        GitTeamID: gitlabGroups.id,
      });

      teams.push(
        ...gitlabSubGroups.map((group) => ({
          TeamName: group.name,
          GitTeamID: group.id,
        }))
      );
    }

    return { teams, error: null };
  } catch (error) {
    console.error('Error importing teams from GitLab:', error);
    return { teams: [], error: error };
  }
}

export async function getTeamMembersFromGl(
  teamId: number,
  authToken: string,
  authUrl?: string
): Promise<{ members: GithubMember[]; error: any }> {
  try {
    const base = resolveGitlabBaseUrl(authUrl);
    const response = await axios.get(
      `${base}/api/v4/groups/${teamId}/members`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      }
    );

    const members = await response.data;

    // Same rule as the GitHub branch: the login is not a name.
    const teamMembers: GithubMember[] = members.map((member: any) => ({
      login: member.username,
      avatar_url: member.avatar_url,
      name: member.name ?? '',
    }));

    return { members: teamMembers, error: null };
  } catch (error) {
    console.error('Error fetching team members from GitLab:', error);
    return { members: [], error: error };
  }
}
